import {ChangeEvent, FC} from "react";
import {useFetchCurrentEvent} from "../../hooks";
import {
    AdditionalServicesFormComponent,
    SelectTypeFormComponent,
    StayDatesCheckboxes,
} from "../form-components";
import {
    AccommodationTypeDto,
    AdditionalServiceDto, FeeTypeDto,
} from "../../types/contracts";
import {
    FieldErrors,
    FieldValues,
    UseFormRegister,
    UseFormWatch,
} from "react-hook-form";
import {singleFormInputsData} from "../../configs";
import FormInput from "../FormInput";
import {useLocation} from "react-router-dom";
import {FormField} from "../../types/enums";

interface FormInputsProps {
    register: UseFormRegister<FieldValues>;
    unregister: (name: string) => void;
    dates: string[];
    additionalServices: AdditionalServiceDto[];
    stayDatesMultiple: string[] | null;
    errors: FieldErrors;
    feeTypes: FeeTypeDto[];
    visibleFields: FormField[];
    defaultVisibleFields: FormField[];
    setVisibleFields: React.Dispatch<React.SetStateAction<FormField[]>>;
    accommodationType: string;
    accommodations: AccommodationTypeDto[];
    watch: UseFormWatch<FieldValues>;
}

export const FormInputs: FC<FormInputsProps> = ({register, unregister, dates, additionalServices, stayDatesMultiple, errors, feeTypes, visibleFields, defaultVisibleFields, setVisibleFields, accommodationType, accommodations, watch,}) => {
    const formFieldValueMap: Record<string, FormField> = Object.values(FormField).reduce(
        (acc, value) => {
            acc[value.toLowerCase()] = value;
            return acc;
        },
        {} as Record<string, FormField>
    );

    const handleFeeTypeChanged = (e: ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;

        const selectedFeeType = feeTypes.find((fee) => fee.id === value);
        console.log("Selected feeType:", selectedFeeType);

        if (value && selectedFeeType?.visibleFormFields) {
            const visibleFieldsFromSelected = selectedFeeType.visibleFormFields
                .map((fieldKey) => {
                    const enumValue = formFieldValueMap[fieldKey.toLowerCase()];
                    if (!enumValue) {
                        console.warn("Unknown FormField key from API:", fieldKey);
                    }
                    return enumValue;
                })
                .filter(Boolean) as FormField[];

            setVisibleFields(visibleFieldsFromSelected);
        } else {
            setVisibleFields(defaultVisibleFields);
        }
    };

    const location = useLocation();
    const {isLoading} = useFetchCurrentEvent();

    return (
        <>
            <h2 className="text-xl font-semibold mb-4">
                {singleFormInputsData.title}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
                {visibleFields.includes(singleFormInputsData.feeTypeData.name) && (
                    <SelectTypeFormComponent
                        onFeeTypeChange={(e: ChangeEvent<HTMLSelectElement>) => handleFeeTypeChanged(e)}
                        register={register}
                        errors={errors}
                        feeTypes={feeTypes}
                        isLoading={isLoading}
                        selectTypeData={singleFormInputsData.feeTypeData}
                        watch={watch}
                    />
                )}

                {singleFormInputsData.basicData.map(
                    (basicData, index) =>
                        visibleFields.includes(basicData.name) && (
                            <FormInput
                                key={index}
                                register={register}
                                errors={errors}
                                basicData={basicData}
                                max={basicData.max}
                            />
                        )
                )}

                {location.pathname === "/form-single" &&
                    visibleFields.includes(
                        singleFormInputsData.accommodationTypeData.name
                    ) && (
                        <SelectTypeFormComponent
                            errors={errors}
                            register={register}
                            selectTypeData={singleFormInputsData.accommodationTypeData}
                            accommodations={accommodations}
                            watch={watch}
                        />
                    )}

                {accommodations.find(
                        (type) => type.isSelfArrangedAccommodation && type.id === accommodationType
                    ) &&
                    singleFormInputsData.accommodationsData.map(
                        (accommodationData, index) => (
                            <FormInput
                                key={index}
                                register={register}
                                errors={errors}
                                basicData={accommodationData}
                            />
                        )
                    )}

                {visibleFields.includes(singleFormInputsData.stayDatesData.name) && (
                    <StayDatesCheckboxes
                        register={register}
                        errors={errors}
                        dates={dates}
                        stayDatesData={singleFormInputsData.stayDatesData}
                    />
                )}

                <div className="sm:col-span-2">
                    {isLoading ? (
                        <p>Načítání doplňkových služeb...</p>
                    ) : (
                        <AdditionalServicesFormComponent
                            additionalServices={additionalServices}
                            register={register}
                            errors={errors}
                            selectedDates={stayDatesMultiple}
                            unregister={unregister}
                            additionalServicesData={
                                singleFormInputsData.additionalServicesData
                            }
                        />
                    )}
                </div>
            </div>
        </>
    );
};
